<template>
  <a-modal
    :visible="visible"
    :destroy-on-close="true"
    title="Edit Question"
    ok-text="Save"
    @ok="() => handleUpdateQuestion()"
    @cancel="$emit('close')"
  >
    <a-form layout="horizontal">
      <SkInput
        v-model="updatedQuestion.title"
        v-validate="'required|max:255'"
        :error="$validator.errors.first('title')"
        data-vv-name="title"
        label="Title"
        placeholder="Title"
      />

      <SkTextarea
        v-model="updatedQuestion.question"
        v-validate="'required'"
        :error="$validator.errors.first('question')"
        :auto-size="{ minRows: 2 }"
        data-vv-name="question"
        label="Question"
        placeholder="Question"
      />

      <a-form-item :label-col="{ span: 8 }" :wrapper-col="{ span: 16 }" label="Onboarding">
        <a-switch v-model="updatedQuestion.onboarding"></a-switch>
      </a-form-item>

      <SkSelect
        v-model="updatedQuestion.quiz_tag_ids"
        :options="quizTags"
        label="Tags"
        mode="multiple"
        placeholder="Please select tags"
        display-prop="name"
      />

      <SkInput
        v-model="updatedQuestion.answer"
        v-validate="'required|max:255'"
        :error="$validator.errors.first('answer')"
        data-vv-name="answer"
        label="Answer"
        placeholder="Answer"
      />
    </a-form>
  </a-modal>
</template>

<script setup lang="ts">
import { ref, watch, toRef, getCurrentInstance } from "vue";
import { QuestionPayloadInterface, QuestionInterface } from "../types";
import { QuizTagInterface } from "@/modules/quiz_tags/types";
import useMixin from "@/useMixin";

// Props
const props = defineProps({
  visible: { type: Boolean, default: false },
  quizTags: { type: Array as () => Array<QuizTagInterface>, default: () => [] },
  originalQuestion: { type: Object as () => QuestionInterface, default: undefined },
});

// Emits
const emits = defineEmits(["get-quiz-tags", "update-question", "close"]);

// Mixins
const { setObject } = useMixin();

// Instances
const instance = getCurrentInstance();
const $validator = instance?.proxy.$validator;

// Data properties
const updatedQuestion = ref<QuestionPayloadInterface>({
  title: undefined,
  question: undefined,
  quiz_tag_ids: [],
  onboarding: false,
  answer: undefined,
});

// Watchers
watch(toRef(props, "visible"), (newProp, oldProp) => {
  if (newProp && !oldProp) {
    emits("get-quiz-tags");

    // Initialize all fields
    updatedQuestion.value = setObject(updatedQuestion.value, props.originalQuestion);
    updatedQuestion.value.quiz_tag_ids = props.originalQuestion.quiz_tags.map(
      (t: QuizTagInterface) => t.id
    );
  }
});

// Class methods
const handleUpdateQuestion = (): void => {
  $validator?.validateAll().then((result) => {
    if (result) {
      emits("update-question", props.originalQuestion.id, updatedQuestion.value);
      emits("close");
    }
  });
};
</script>
